import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useModal } from 'react-modal-hook';
import { Field, Form, withFormik } from 'formik';
import { graphql, useStaticQuery } from 'gatsby';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { gaTrackSignupModalOpened } from 'utils/google-analytics/gaTracking';
import { hasEmailTypo } from 'utils/helpers/emailCheck';

import ButtonGoogleSignIn from 'components/buttons/button-google-sign-in';
import FontIcon from 'components/icons';
import CustomCalendar from 'components/modal/custom-calendar';
import ScheduleDemoModal from 'components/modal/schedule-demo/schedule-demo-modal';

import './styles/signups.scss';
import Button from '../buttons/button';
import CloseBtn from '../buttons/button-close';
import SignupForm from '../forms/signup-full';

ReactModal.setAppElement('#___gatsby');

const fullNameRegExp = /\w+\s\w+/;
const emailRegExp = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

type ModalSignupProps = {
  buttonArrow?: boolean,
  buttonClasses?: string | boolean,
  buttonInline?: boolean,
  ctaVariation?: string,
  customHubspotMeetingButtonText?: string,
  customHubspotMeetingUrl?: string,
  fieldLayout?: string,
  footerSignupButtonText?: any,
  isFixed?: boolean,
  isZapier?: boolean,
  legalText?: string | ReactNode,
  lockPlanId?: boolean,
  longPlaceholder?: boolean,
  modalOpenCallback?: () => void,
  modalTitle?: string | ReactNode,
  onSuccessfulSignup?: any,
  prefilledValues?: {
    email: string,
    name: string
  },
  refPlanId?: string,
  showCustomCalendar?: boolean,
  showIcons?: boolean,
  showInitFields?: boolean,
  showModalButton?: boolean,
  showScheduleDemoButton?: boolean,
  signupClasses?: string,
  signupModalLaunch?: string,
  signupModalSubmit?: string,
  targetModule?: boolean,
  trialLength?: number,
  uniqueId?: string
}

type InitialFormProps = {
  alignForm?: any,
  buttonArrow?: boolean,
  buttonClasses?: any,
  buttonInline?: any,
  ctaVariation?: any,
  customHubspotMeetingButtonText?: any,
  customHubspotMeetingUrl?: any,
  email: string,
  errors?: any,
  fieldLayout?: any,
  footerSignupButtonText?: any,
  fullName: string,
  isFixed?: any,
  longPlaceholder?: any,
  refPlanId?: any,
  setErrors?: any,
  showCustomCalendar?: any,
  showIcons?: any,
  showInitFields?: any,
  showModal?: any,
  showModalButton?: any,
  showScheduleDemoButton?: any,
  signupClasses?: any,
  signupModalLaunch?: any,
  submitCount?: any,
  touched?: any,
  trialLength?: number,
  uniqueId?: any,
  // eslint-disable-next-line react/no-unused-prop-types
  validateOnBlur?: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  validateOnChange?: boolean,
  values?: any
}
const ModalSignup = ({
  signupModalSubmit,
  fieldLayout,
  buttonInline,
  showIcons,
  targetModule,
  uniqueId,
  isFixed,
  buttonClasses,
  isZapier,
  refPlanId,
  lockPlanId,
  trialLength,
  onSuccessfulSignup,
  modalOpenCallback,
  prefilledValues,
  modalTitle,
  signupClasses,
  longPlaceholder,
  showCustomCalendar,
  customHubspotMeetingButtonText,
  customHubspotMeetingUrl,
  showScheduleDemoButton,
  showModalButton,
  footerSignupButtonText,
  signupModalLaunch,
  ctaVariation,
  legalText,
  showInitFields,
  buttonArrow,
}:ModalSignupProps) => {

  const data = useStaticQuery(graphql`
    {
      allWordpressPage(filter: { slug: { eq: "signup" } }) {
        edges {
          node {
            acf {
              sign_up {
                legal
              }
            }
          }
        }
      }
    }
  `);

  const pageContent = data.allWordpressPage.edges[0].node.acf;
  const title = modalTitle;
  const legalCopy = legalText || pageContent.sign_up.legal;
  const isUniqueId = uniqueId || '';
  const ctaVariationIntialFields = showInitFields ? 'Page CTA + modal' : 'Single modal';

  const [emailAddress, setEmailAddress] = useState(
    prefilledValues ? prefilledValues.email ?? '' : ''
  );
  const [fullNameIs, setFullNameIs] = useState(
    prefilledValues ? prefilledValues.name ?? '' : ''
  );

  // @ts-ignore
  const [showModal, hideModal] = useModal(
    ({ in: open, onExited }:any) => {
      const ldClient = useLDClient();

      const trackData = {
        'sign up domain': 'wheniwork.com',
      };
      // @ts-ignore
      ldClient.track('Sign up form started', trackData);

      trackData['Signed up with SSO'] = false;
      trackData['SSO Type'] = 'None';
      trackData['CTA variation'] = ctaVariation || ctaVariationIntialFields;
      trackData['CTA # of steps'] = showInitFields ? 2 : 1;
      window.posthog?.capture('Click to sign up', trackData);

      const flushLD = () => {
        // @ts-ignore
        ldClient.flush();
        hideModal();
      };

      return (
        <ReactModal
          isOpen
          // @ts-ignore
          open={ open && (gaTrackSignupModalOpened() &&  modalOpenCallback ? modalOpenCallback() : undefined) }
          onExited={ onExited }
          onRequestClose={ flushLD }
          className="default-modal-wrapper signup-modal"
          overlayClassName="default-modal-overlay"
        >
          <div className="signupModal signups-container" data-section="signupModal">
            <CloseBtn onClick={ flushLD } />
            <SignupForm
              fullName={ fullNameIs }
              email={ emailAddress }
              signupModalSubmit={ signupModalSubmit }
              legalText={ legalCopy }
              fieldLayout={ fieldLayout }
              showInitFields={ showInitFields }
              targetModule={ targetModule }
              uniqueId={ isUniqueId }
              isFixed={ isFixed }
              isZapier={ isZapier }
              refPlanId={ refPlanId }
              lockPlanId={ lockPlanId }
              trialLength={ trialLength }
              onSuccessfulSignup={ onSuccessfulSignup }
              title={ typeof title === 'undefined' ? 'Let\'s get the schedule set up for your workplace.' : title }
              ctaVariation={ ctaVariation || ctaVariationIntialFields }
              buttonArrow={ buttonArrow }
            />
          </div>
        </ReactModal> as any
      );
    },
    [
      title,
      showInitFields,
      fieldLayout,
      fullNameIs,
      emailAddress,
      signupModalSubmit,
      legalText,
      isUniqueId,
      isFixed,
      isZapier,
      onSuccessfulSignup,
    ]
  );

  const FormikInitialForm = withFormik({
    mapPropsToValues({ fullName, email }:InitialFormProps) {
      return {
        fullName: fullName || '',
        email: email || '',
      };
    },
    validate: values => {
      const errors = {} as InitialFormProps;

      switch (true) {
        case !values.fullName || values.fullName === '':
          errors.fullName = 'Full Name Required';
          break;

        case !fullNameRegExp.test(values.fullName):
          errors.fullName = 'Invalid Full Name';
          break;

        case !values.email || values.email === '':
          errors.email = 'Email Address Required';
          break;

        case !emailRegExp.test(values.email):
          errors.email = 'Invalid Email Address';
          break;

        case hasEmailTypo(values.email):
          errors.email = 'Invalid Email Address';
          break;
      }

      return errors;
    },
    handleSubmit(values) {
      setFullNameIs(values.fullName),
      setEmailAddress(values.email),
      showModal();
    },
  })(InitialForm);

  return (
    <FormikInitialForm
      validateOnBlur={ false }
      validateOnChange={ false }
      fullName={ fullNameIs }
      email={ emailAddress }
      buttonClasses={ buttonClasses }
      signupModalLaunch={ signupModalLaunch }
      showInitFields={ showInitFields }
      footerSignupButtonText={ footerSignupButtonText }
      showModalButton={ showModalButton }
      showScheduleDemoButton={ showScheduleDemoButton }
      customHubspotMeetingUrl={ customHubspotMeetingUrl }
      customHubspotMeetingButtonText={ customHubspotMeetingButtonText }
      showCustomCalendar={ showCustomCalendar }
      fieldLayout={ fieldLayout }
      buttonInline={ buttonInline }
      showModal={ showModal }
      showIcons={ showIcons }
      longPlaceholder={ longPlaceholder }
      uniqueId={ uniqueId }
      isFixed={ isFixed }
      signupClasses={ signupClasses }
      ctaVariation={ ctaVariation || ctaVariationIntialFields }
      refPlanId={ refPlanId }
      trialLength={ trialLength }
      buttonArrow={ buttonArrow }
    />
  );
};

const InitialForm = ({
  errors,
  touched,
  setErrors,
  values,
  buttonClasses,
  showInitFields,
  showScheduleDemoButton,
  customHubspotMeetingUrl,
  customHubspotMeetingButtonText,
  showCustomCalendar,
  showModalButton,
  footerSignupButtonText,
  showIcons,
  longPlaceholder,
  signupModalLaunch,
  fieldLayout,
  buttonInline,
  showModal,
  refPlanId,
  trialLength,
  uniqueId,
  isFixed,
  submitCount,
  signupClasses,
  alignForm,
  ctaVariation,
  buttonArrow,
}:InitialFormProps) => {

  const [hideErrorMessage, setHideErrorMessage] = useState(true);
  const [highlightInput, updateHighlightInput] = useState(true);

  const [showScheduleDemoModal, hideScheduleDemoModal] = useModal(() => (
    <ScheduleDemoModal isOpen={ true } onHide={ hideScheduleDemoModal } />
  ));

  const timerRef:any = useRef(null);
  
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      // if there are errors show them
      setHideErrorMessage(false);
      updateHighlightInput(false);
      document && document?.getElementById(`${Object.keys(errors)[0]}${uniqueId}`)?.focus();
      setTimer();
    }
  }, [errors]);

  useEffect(() => {
    setHideErrorMessage(true); // on input value change hide the error message
    updateHighlightInput(true); // on input value change hide the red outline
    stopTimer();
  }, [values]);

  function setTimer() {
    if (Object.keys(errors).length > 0) {
      timerRef.current = setTimeout(() => {
        setHideErrorMessage(true);
      }, 3000);
    }
  }

  function setTimerOnClick() {
    if (timerRef.current) {
      stopTimer();
    }
    if (Object.keys(errors).length > 0 && submitCount > 0) {
      // on all submissions after the first one if there are errors show them on click
      setHideErrorMessage(false);
      updateHighlightInput(false);
      document && document?.getElementById(`${Object.keys(errors)[0]}${uniqueId}`)?.focus();
      setTimer();
    }
  }

  function blurHandler() {
    // on blur of input with current error message hide message
    setHideErrorMessage(true);
    updateHighlightInput(true);
    if (timerRef.current) {
      stopTimer();
    }
  }

  function stopTimer() {
    clearTimeout(timerRef.current);
    // setHideErrorMessage(true);
  }

  useEffect(() => {
    emailRegExp.test(values.email) && errors.email ? setErrors({}) : '';
    fullNameRegExp.test(values.fullName) && errors.fullName
      ? setErrors({})
      : '';
  }, [values]);
  
  return (
    <Fragment>
      {showInitFields && (
        <div
          className={ `signups-container ${
            signupClasses ? signupClasses : ''
          }` }
        >
          <div
            className={ `form-trigger align-${alignForm} ${
              fieldLayout === 'row' ? ' field-row' : ''
            }` }
          >
            <Form noValidate data-section="signupForm">
              <div
                className={ `columns is-variable is-1 ${
                  fieldLayout === 'row'
                    ? ' is-vcentered'
                    : ' is-justified-center is-column'
                } ` }
              >
                <div
                  className={ `column has-margin-x-auto ${
                    fieldLayout === 'column' ? 'is-12-tablet' : ''
                  }` }
                >
                  <label htmlFor={ `fullName${uniqueId}` } className="is-sr-only">
                    Full Name
                  </label>
                  <div className="control">
                    <Field
                      className={ `input input-light name is-medium ${
                        showIcons === true ? 'hasInputIcon' : ''
                      } ${fieldLayout === 'column' ? ' is-12-tablet' : ''}` }
                      id={ `fullName${uniqueId}` }
                      title="Full Name"
                      aria-placeholder="Please enter your first and last name"
                      type="text"
                      name="fullName"
                      onBlur={ blurHandler }
                      placeholder={ `${
                        longPlaceholder === true
                          ? 'What is your full name?'
                          : 'Full Name'
                      }` }
                      style={
                        touched.fullName &&
                        errors.fullName && { border: '2px solid red' }
                      }
                    />
                    <span
                      className={ `icon is-small is-left inputIcons ${
                        showIcons === true ? '' : 'is-hidden'
                      }` }
                    >
                      <FontIcon icon="user" />
                    </span>
                    {touched.fullName && errors.fullName && (
                      <div
                        className={ `${
                          isFixed ? 'errors-tooltip-above' : 'errors-tooltip'
                        } ${
                          document && document?.activeElement?.id === `fullName${uniqueId}` &&
                          hideErrorMessage === false
                            ? ''
                            : 'is-hidden'
                        }` }
                      >
                        <p className="tooltip-inner has-text-danger">
                          <span className="icon has-text-warning tooltip-icon">
                            <FontIcon icon="warning" />
                          </span>
                          <span className="tooltip-text">
                            {errors.fullName}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={ `column has-margin-x-auto ${
                    fieldLayout === 'column' ? 'is-12-tablet' : ''
                  }` }
                >
                  <label htmlFor={ `email${uniqueId}` } className="is-sr-only">
                    Email Address
                  </label>
                  <div
                    className={ `control ${
                      fieldLayout === 'row' ? ' is-expanded' : ''
                    }` }
                  >
                    <Field
                      className={ `input input-light name is-medium ${
                        showIcons === true ? 'hasInputIcon' : ''
                      }` }
                      id={ `email${uniqueId}` }
                      type="email"
                      name="email"
                      title="Email Address"
                      aria-placeholder="Please enter your email address"
                      onBlur={ blurHandler }
                      placeholder={ `${
                        longPlaceholder === true
                          ? 'What is your work email?'
                          : 'Email Address'
                      }` }
                      style={
                        touched.email &&
                        errors.email && { border: '2px solid red' }
                      }
                    />
                    <span
                      className={ `icon is-small is-left inputIcons ${
                        showIcons === true ? '' : 'is-hidden'
                      }` }
                    >
                      <FontIcon icon="email" />
                    </span>
                    {touched.email && errors.email && (
                      <div
                        className={ `${
                          isFixed ? 'errors-tooltip-above' : 'errors-tooltip'
                        } ${
                          document && document?.activeElement?.id === `email${uniqueId}` &&
                          hideErrorMessage === false
                            ? ''
                            : 'is-hidden'
                        }` }
                      >
                        <p className="tooltip-inner has-text-danger">
                          <span className="icon has-text-warning tooltip-icon">
                            <FontIcon icon="warning" />
                          </span>
                          <span className="tooltip-text">{errors.email}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {buttonInline && (
                  <div
                    className={ `column has-margin-x-auto ${
                      fieldLayout === 'column' ? 'is-12-tablet' : ''
                    }` }
                  >
                    <div className="control">
                      <div className="field">
                        <Button
                          className={ `button button-submit ${buttonClasses}` }
                          type="submit"
                          title="Enable Modal"
                          aria-label={ signupModalLaunch || 'Start Free Trial' }
                          onClick={ setTimerOnClick }
                          text={ signupModalLaunch || 'Start Free Trial' }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {!buttonInline && (
                <div className="field">
                  <div className="control">
                    <Button
                      className={ `button button-submit ${buttonClasses}` }
                      type="submit"
                      title="Enable Modal"
                      aria-label={ signupModalLaunch || 'Start Free Trial' }
                      onClick={ setTimerOnClick }
                      text={ signupModalLaunch || 'Start Free Trial' }
                    />
                  </div>
                </div>
              )}
            </Form>
          </div>
          { !isFixed &&
            <Fragment>
              <div className="or-separator"><span>OR</span></div>
              <ButtonGoogleSignIn
                ctaVariation={ ctaVariation }
                numSteps={ showInitFields ? 2 : 1 }
                refPlanId={ refPlanId }
                trialLength={ trialLength }
              />
            </Fragment>
          }
        </div>
      )}

      {showModalButton && (
        <div
          className={ `signups-container ${
            signupClasses ? signupClasses : ''
          }` }
          data-section="footerSignupSection"
        >
          <Button
            className={ buttonClasses }
            type="button"
            title="Enable Modal"
            aria-label={ signupModalLaunch || 'Start Free Trial' }
            onClick={ showModal }>
            {signupModalLaunch || 'Start Free Trial'}
            {buttonArrow && <FontIcon icon="arrow-right" />}
          </Button>
        </div>
      )}
      {typeof customHubspotMeetingUrl !== 'undefined' && customHubspotMeetingUrl !== '' && customHubspotMeetingUrl !== null && <CustomCalendar customCalendarUrl={ customHubspotMeetingUrl } buttonModalLaunchText={ customHubspotMeetingButtonText ?? 'Schedule Demo' } />}
      {showScheduleDemoButton && (
        <div
          className={ `signups-container ${
            signupClasses ? signupClasses : ''
          }` }
          data-section="footerIntercomSection"
        >
          <Button
            className={ `intercom-modal-button ${buttonClasses}` }
            type="button"
            title="Enable Modal"
            aria-label={ signupModalLaunch || 'Start Free Trial' }
            onClick={ showScheduleDemoModal }
            text={ footerSignupButtonText || 'Start Free Trial' }
          />
        </div>
      )}
      {showCustomCalendar && showCustomCalendar && (
        <div
          className={ `signups-container ${
            signupClasses ? signupClasses : ''
          }` }
          data-section="footerIntercomSection"
        >
          <Button
            className={ `intercom-modal-button ${buttonClasses}` }
            type="button"
            title="Enable Modal"
            aria-label={ signupModalLaunch || 'Start Free Trial' }
            onClick={ showScheduleDemoModal }
            text={ footerSignupButtonText || 'Start Free Trial' }
          />
        </div>
      )}
    </Fragment>
  );
};

export default ModalSignup;